import { useRef } from "react";

/** Порог смахивания (px): дальше — оператор снимается с расстановки. */
const SWIPE_REMOVE = 56;

/** Жесты чипа оператора (21.08, Саша — перенос «вырезать» УДАЛЁН, он конфликтовал
 *  со всем): long-press 450мс → onMenu(id); сдвиг до long-press — СМАХИВАНИЕ:
 *  чип едет за пальцем и тает; дальше порога — анимированный «улёт» по вектору
 *  жеста и onSwipeAway (удаление), ближе — плавный возврат-«примагничивание»
 *  на место. Каждый новый жест начинается со сброса остаточных сдвигов, чтобы
 *  чип никогда не застревал сдвинутым. */
export function useChipDrag({ onMenu, onSwipeAway }: {
  onMenu: (id: string) => void;
  onSwipeAway: (opId: string) => void;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const holdId = useRef<string | null>(null);
  const swipe = useRef<{ id: string; el: HTMLElement; dist: number; dx: number; dy: number } | null>(null);
  const chipEl = useRef<HTMLElement | null>(null);

  const clearTimer = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };

  const chipDown = (id: string, e: React.PointerEvent) => {
    start.current = { x: e.clientX, y: e.clientY };
    chipEl.current = e.currentTarget as HTMLElement;
    /* магнит: остаточные сдвиги прошлых жестов снимаются до начала нового */
    chipEl.current.style.transform = ""; chipEl.current.style.opacity = ""; chipEl.current.style.transition = "";
    try { (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId); } catch { /* синтетика/старые браузеры */ }
    timer.current = setTimeout(() => { onMenu(id); holdId.current = id; }, 450);
  };
  const chipMove = (id: string, e: { clientX: number; clientY: number }) => {
    const s = start.current;
    if (!s || holdId.current === id) return; // меню открыто — чип не двигаем
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (swipe.current) { // смахивание: чип за пальцем, тает по расстоянию
      const sw = swipe.current; sw.dist = Math.hypot(dx, dy); sw.dx = dx; sw.dy = dy;
      sw.el.style.transform = `translate(${dx}px, ${dy}px)`;
      sw.el.style.opacity = String(Math.max(0.25, 1 - sw.dist / 140));
      return;
    }
    if (Math.abs(dx) <= 10 && Math.abs(dy) <= 10) return;
    if (chipEl.current) { clearTimer(); swipe.current = { id, el: chipEl.current, dist: 0, dx, dy }; }
  };
  const chipRelease = () => {
    clearTimer(); holdId.current = null;
    const sw = swipe.current;
    if (!sw) return;
    swipe.current = null;
    const el = sw.el;
    if (sw.dist > SWIPE_REMOVE) { // «улёт» по вектору жеста + гарантия от застревания
      const k = 1 + 200 / Math.max(sw.dist, 1);
      el.style.transition = "transform 0.2s ease-in, opacity 0.2s ease-in";
      el.style.transform = `translate(${sw.dx * k}px, ${sw.dy * k}px) scale(0.6) rotate(${sw.dx < 0 ? -10 : 10}deg)`;
      el.style.opacity = "0";
      setTimeout(() => onSwipeAway(sw.id), 180);
      setTimeout(() => { el.style.cssText = ""; }, 500); // узел пережил удаление → примагнитить обратно
      return;
    }
    el.style.transition = "transform 0.18s cubic-bezier(0.2, 0.9, 0.3, 1.3), opacity 0.15s"; // возврат-«магнит»
    el.style.transform = ""; el.style.opacity = "";
    setTimeout(() => { el.style.transition = ""; }, 220);
  };

  return { chipDown, chipMove, chipRelease };
}
